import { useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { api } from './api';
import { displayDate } from './dates';
import { i18n } from './i18n';
import { useOrgId } from './org';

export type VatStatus = 'open' | 'due' | 'overdue' | 'filed' | 'paid';

export interface VatSettings {
  vatRegistered: boolean;
  trn: string | null;
  registrationDate: string | null;
  emirate: string | null;
  frequency: 'monthly' | 'quarterly';
  quarterStartMonth: number;
  largePurchaseThreshold: string;
  disclosureThreshold: string;
  currency: string;
}

export interface VatReturnSummary {
  key: string;
  id: string | null;
  periodStart: string;
  periodEnd: string;
  label: string;
  dueDate: string;
  status: VatStatus;
  outputVat: string;
  inputVat: string;
  netVat: string;
  settled: string;
  filedOn: string | null;
  exceptions: number | null;
}

export interface VatBox {
  box: string;
  label: string;
  amount: string | null;
  vat: string | null;
  adjAmount: string | null;
  adjVat: string | null;
  totalAmount: string | null;
  totalVat: string | null;
}

export interface VatAdjustment {
  id: string;
  box: string;
  amount: string;
  vat: string;
  reason: string;
  createdBy: string | null;
  createdAt: string;
}

export interface VatPayment {
  id: string;
  kind: 'payment' | 'refund';
  date: string;
  amount: string;
  bankAccountId: string;
  bankAccountName: string;
  entryId: string;
  entryNo: string;
  reference: string | null;
}

export interface VatReturn extends VatReturnSummary {
  orgName: string;
  trn: string | null;
  currency: string;
  boxes: VatBox[];
  adjustments: VatAdjustment[];
  payments: VatPayment[];
  filing: {
    filedAt: string;
    filedBy: string | null;
    reference: string | null;
    returnEntryId: string | null;
    returnEntryNo: string | null;
    changedSinceFiling: boolean;
  } | null;
  bookedNetVat: string;
  canFile: boolean;
  canReopen: boolean;
}

export interface VatLine {
  lineId: string;
  entryId: string;
  entryNo: string;
  date: string;
  sourceType: string;
  memo: string | null;
  description: string | null;
  contactName: string | null;
  contactTrn: string | null;
  taxCode: string;
  box: string;
  amount: string;
  vat: string;
}

export interface VatException {
  kind: string;
  severity: 'error' | 'warning';
  title: string;
  message: string;
  entryId: string | null;
  entryNo: string | null;
  date: string | null;
  sourceType: string | null;
  memo: string | null;
  contactName: string | null;
  amount: string | null;
}

export function useVatSettings() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['vat', orgId, 'settings'],
    queryFn: () => api<VatSettings>(`/orgs/${orgId}/vat/settings`),
    enabled: Boolean(orgId),
  });
}

export function useVatReturns() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['vat', orgId, 'returns'],
    queryFn: () => api<VatReturnSummary[]>(`/orgs/${orgId}/vat/returns`),
    enabled: Boolean(orgId),
  });
}

export function useVatReturn(key: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['vat', orgId, 'return', key],
    queryFn: () => api<VatReturn>(`/orgs/${orgId}/vat/returns/${key}`),
    enabled: Boolean(orgId),
  });
}

export function useVatExceptions(key: string) {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['vat', orgId, 'return', key, 'exceptions'],
    queryFn: () => api<VatException[]>(`/orgs/${orgId}/vat/returns/${key}/exceptions`),
    enabled: Boolean(orgId),
  });
}

const DAY = 86_400_000;
const utc = (iso: string) => Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10));

/** Status chip text and tone (document 05: Open / Due in n days / Overdue / Filed / Paid). */
export function vatStatusChip(
  r: Pick<VatReturnSummary, 'status' | 'dueDate'>,
  today: string,
): { label: string; tone: 'info' | 'warning' | 'danger' | 'success' | 'neutral' } {
  switch (r.status) {
    case 'open':
      return { label: i18n.t('taxPages.vatTabs.statusOpen'), tone: 'info' };
    case 'due': {
      const days = Math.round((utc(r.dueDate) - utc(today)) / DAY);
      return {
        label:
          days === 0
            ? i18n.t('taxPages.vatTabs.statusDueToday')
            : i18n.t('taxPages.vatTabs.statusDueIn', { count: days }),
        tone: 'warning',
      };
    }
    case 'overdue':
      return { label: i18n.t('taxPages.vatTabs.statusOverdue'), tone: 'danger' };
    case 'filed':
      return { label: i18n.t('taxPages.vatTabs.statusFiled'), tone: 'neutral' };
    case 'paid':
      return { label: i18n.t('taxPages.vatTabs.statusPaid'), tone: 'success' };
  }
}

export const TONE_CLASS: Record<ReturnType<typeof vatStatusChip>['tone'], string> = {
  info: 'border-[#175CD3]/30 bg-[#175CD3]/10 text-[#175CD3]',
  warning: 'border-[#B45309]/30 bg-[#B45309]/10 text-[#B45309]',
  danger: 'border-[#B42318]/30 bg-[#B42318]/10 text-[#B42318]',
  success: 'border-[#1E7F4F]/30 bg-[#1E7F4F]/10 text-[#1E7F4F]',
  neutral: 'border-border bg-muted text-foreground',
};

/** Totals a box drills into (the API's DRILL map); other boxes drill into themselves. */
export const BOX_DRILL: Record<string, string[]> = {
  '8': ['1a', '1b', '1c', '1d', '1e', '1f', '1g', '2', '3', '4', '5', '6', '7'],
  '11': ['9', '10'],
  '12': ['1a', '1b', '1c', '1d', '1e', '1f', '1g', '2', '3', '4', '5', '6', '7'],
  '13': ['9', '10'],
  '14': ['1a', '1b', '1c', '1d', '1e', '1f', '1g', '2', '3', '4', '5', '6', '7', '9', '10'],
};
export const drillBoxes = (box: string): string[] => BOX_DRILL[box] ?? [box];

/** Boxes an accountant may adjust (never the totals). */
export const ADJUSTABLE_BOXES = [
  '1a',
  '1b',
  '1c',
  '1d',
  '1e',
  '1f',
  '1g',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '9',
  '10',
];

/** Section a box belongs to on the FTA form. */
export function boxSection(box: string): 'sales' | 'expenses' | 'net' {
  if (['9', '10', '11'].includes(box)) return 'expenses';
  if (['12', '13', '14'].includes(box)) return 'net';
  return 'sales';
}

/** "-1,234.50" style plain negation for a decimal string. */
export const negate = (d: string) => (d.startsWith('-') ? d.slice(1) : d === '0.00' ? d : `-${d}`);

/** CSV of the VAT 201 working in FTA box order (opens in Excel). */
export function vatReturnCsv(r: VatReturn): string {
  const rows: Array<Array<string>> = [
    [r.orgName],
    [i18n.t('accountingPages.vatCsv.trnLine', { trn: r.trn ?? '' })],
    [
      i18n.t('accountingPages.vatCsv.title', {
        label: r.label,
        from: displayDate(r.periodStart),
        to: displayDate(r.periodEnd),
      }),
    ],
    [],
    [
      i18n.t('accountingPages.vatCsv.box'),
      i18n.t('accountingPages.vatCsv.description'),
      i18n.t('accountingPages.vatCsv.amountIn', { currency: r.currency }),
      i18n.t('accountingPages.vatCsv.vatIn', { currency: r.currency }),
      i18n.t('accountingPages.vatCsv.adjAmount'),
      i18n.t('accountingPages.vatCsv.adjVat'),
      i18n.t('accountingPages.vatCsv.totalAmount'),
      i18n.t('accountingPages.vatCsv.totalVat'),
    ],
    ...r.boxes.map((b) => [
      b.box,
      b.label,
      b.amount ?? '',
      b.vat ?? '',
      b.adjAmount ?? '',
      b.adjVat ?? '',
      b.totalAmount ?? '',
      b.totalVat ?? '',
    ]),
  ];
  if (r.adjustments.length) {
    rows.push(
      [],
      [i18n.t('accountingPages.vatCsv.adjustments')],
      [
        i18n.t('accountingPages.vatCsv.box'),
        i18n.t('accountingPages.vatCsv.amount'),
        i18n.t('accountingPages.vatCsv.vat'),
        i18n.t('accountingPages.vatCsv.reason'),
        i18n.t('accountingPages.vatCsv.by'),
      ],
    );
    for (const a of r.adjustments) rows.push([a.box, a.amount, a.vat, a.reason, a.createdBy ?? '']);
  }
  return Papa.unparse(rows);
}

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const VAT_PRESETS = [
  { key: 'this-quarter', label: 'This quarter' },
  { key: 'last-quarter', label: 'Last quarter' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-year', label: 'This year' },
  { key: 'last-year', label: 'Last year' },
] as const;

/** Calendar date ranges for the VAT report presets. */
export function vatPresetRange(key: string, today: string): { from: string; to: string } | null {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7)) - 1;
  const iso = (yy: number, mm: number, dd: number) => {
    const d = new Date(Date.UTC(yy, mm, dd));
    return d.toISOString().slice(0, 10);
  };
  const q = Math.floor(m / 3) * 3;
  switch (key) {
    case 'this-quarter':
      return { from: iso(y, q, 1), to: iso(y, q + 3, 0) };
    case 'last-quarter':
      return { from: iso(y, q - 3, 1), to: iso(y, q, 0) };
    case 'this-month':
      return { from: iso(y, m, 1), to: iso(y, m + 1, 0) };
    case 'last-month':
      return { from: iso(y, m - 1, 1), to: iso(y, m, 0) };
    case 'this-year':
      return { from: iso(y, 0, 1), to: iso(y, 11, 31) };
    case 'last-year':
      return { from: iso(y - 1, 0, 1), to: iso(y - 1, 11, 31) };
    default:
      return null;
  }
}
